// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import type { AriaAttributes, InputHTMLAttributes, ReactNode, Ref, TextareaHTMLAttributes } from 'react';

import { cx } from './cx';
import styles from './TextInput.module.css';

/** `filled`: a borderless tinted well (Bygning). `outline`: a sunken bordered field. Same as `SelectVariant`. */
export type FieldVariant = 'filled' | 'outline';
/**
 * `sm` — dense (28px): forms, dialogs, table cells, settings, legacy screens.
 * `md` — bar (32px): toolbars.
 * `lg` — field (32px, body text): the inspector's FieldRow controls, Sagsdata.
 * Same as `SelectSize`, phone growth included.
 */
export type FieldSize = 'sm' | 'md' | 'lg';

export interface TextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  /** Default `outline`, as Select. */
  variant?: FieldVariant;
  /** Default `sm`, as Select. */
  size?: FieldSize;
  /** Fill the container's width. */
  block?: boolean;
  /** Monospace, tabular figures (quantities, ids, paths). */
  mono?: boolean;
  /** A unit or other trailing text inside the well (m², stk). */
  suffix?: ReactNode;
  /** A leading icon inside the well (SearchInput's magnifier); aria-hidden. */
  prefix?: ReactNode;
  ref?: Ref<HTMLInputElement>;
}

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  variant?: FieldVariant;
  size?: FieldSize;
  block?: boolean;
  ref?: Ref<HTMLTextAreaElement>;
}

function isTrue(v: AriaAttributes['aria-invalid']): boolean {
  return v === true || v === 'true';
}

interface WrapState {
  variant: FieldVariant;
  size: FieldSize;
  block?: boolean;
  mono?: boolean;
  area?: boolean;
  className?: string;
}

function wrapClass({ variant, size, block, mono, area, className }: WrapState): string {
  return cx(
    styles.wrap,
    styles[variant],
    styles[size],
    block && styles.block,
    mono && styles.mono,
    area && styles.area,
    className,
  );
}

/**
 * The shared single-line text field. As with Select, `className` and `style`
 * land on the wrapper (the one width rule) — here the wrapper is also the
 * well, so `suffix` sits inside it. Every other prop (value, onChange, ref,
 * aria-*) goes to the `<input>`.
 */
export function TextInput({
  variant = 'outline',
  size = 'sm',
  block,
  mono,
  suffix,
  prefix,
  className,
  style,
  disabled,
  readOnly,
  ...rest
}: TextInputProps) {
  const hasPrefix = prefix != null && prefix !== false;
  return (
    <span
      className={wrapClass({ variant, size, block, mono, className })}
      style={style}
      data-disabled={disabled || undefined}
      data-readonly={readOnly || undefined}
      data-invalid={isTrue(rest['aria-invalid']) || undefined}
      data-prefix={hasPrefix || undefined}
    >
      {hasPrefix && (
        <span className={styles.prefix} aria-hidden="true">
          {prefix}
        </span>
      )}
      <input className={styles.control} disabled={disabled} readOnly={readOnly} {...rest} />
      {suffix != null && suffix !== false && <span className={styles.suffix}>{suffix}</span>}
    </span>
  );
}

/**
 * A `TextInput` for search: `type="search"`, the keyboard's search key, no
 * WebKit decoration, the magnifier in front. `prefix` replaces the
 * magnifier; `prefix={null}` hides it.
 */
export function SearchInput({ enterKeyHint = 'search', prefix, ...props }: Omit<TextInputProps, 'type'>) {
  return (
    <TextInput type="search" enterKeyHint={enterKeyHint} prefix={prefix === undefined ? <SearchIcon /> : prefix} {...props} />
  );
}

/** The one search magnifier: a 16px stroked circle and handle. */
export function SearchIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="7" cy="7" r="4.25" />
      <path d="M10.25 10.25 13 13" />
    </svg>
  );
}

/**
 * The multi-line twin of `TextInput`: same variants, sizes and wrapper rule;
 * resizes vertically, three rows by default.
 */
export function TextArea({
  variant = 'outline',
  size = 'sm',
  block,
  className,
  style,
  disabled,
  readOnly,
  rows = 3,
  ...rest
}: TextAreaProps) {
  return (
    <span
      className={wrapClass({ variant, size, block, area: true, className })}
      style={style}
      data-disabled={disabled || undefined}
      data-readonly={readOnly || undefined}
      data-invalid={isTrue(rest['aria-invalid']) || undefined}
    >
      <textarea className={styles.control} disabled={disabled} readOnly={readOnly} rows={rows} {...rest} />
    </span>
  );
}
